import { useMemo } from 'react'

import useModal from 'hooks/use-modal'

import Button, { ButtonProps, ButtonVariant } from '../Button'
import IconButton from '../IconButton'
import { ActionVariant as ConfirmationVariant } from '../../ConfirmationModal'

export type Props = Omit<ButtonProps, 'icon' | 'iconColor' | 'variant'> & {
  variant:
    | 'save'
    | 'edit'
    | 'delete'
    | 'cancel'
    | 'create'
    | 'deactivate'
    | 'reactivate'
    | 'add'
    | 'reject'
    | 'approve'
    | 'download'
    | 'change'
  isVisible?: boolean
  withConfirmation?: boolean
  buttonVariant?: ButtonVariant
  isIconOnly?: boolean
}

const ActionButton = ({
  name,
  variant,
  isVisible = true,
  withConfirmation = false,
  onClick,
  buttonVariant,
  isIconOnly,
  ...props
}: Props) => {
  const { setConfirmationModal } = useModal()

  const iconProps: Partial<ButtonProps> = useMemo(() => {
    if (variant === 'save') {
      return {
        variant: buttonVariant ?? 'fill',
      }
    }
    if (variant === 'create') {
      return {
        variant: buttonVariant ?? 'fill',
        icon: ['fas', 'plus'],
        iconColor: 'white',
        color: 'white',
      }
    }
    if (variant === 'edit') {
      return {
        variant: buttonVariant ?? 'outline',
        icon: ['fal', 'pencil'],
        iconColor: 'blue1',
      }
    }
    if (variant === 'delete') {
      return {
        variant: buttonVariant ?? 'outline',
        icon: ['far', 'trash-alt'],
        color: 'red',
        iconColor: 'red',
      }
    }
    if (variant === 'deactivate') {
      return {
        variant: buttonVariant ?? 'outline',
        icon: ['far', 'minus-octagon'],
        color: 'red',
        iconColor: 'red',
      }
    }
    if (variant === 'change') {
      return {
        variant: buttonVariant ?? 'outline',
        icon: ['far', 'arrows-to-dotted-line'],
        color: 'blue1',
        iconColor: 'blue1',
      }
    }
    if (variant === 'reactivate') {
      return {
        variant: buttonVariant ?? 'outline',
        icon: ['far', 'repeat'],
      }
    }
    if (variant === 'cancel') {
      return {
        variant: buttonVariant ?? 'ghost',
      }
    }
    if (variant === 'add') {
      return {
        variant: buttonVariant ?? 'outline',
        icon: ['fas', 'plus'],
      }
    }
    if (variant === 'reject') {
      return {
        variant: buttonVariant ?? 'error',
      }
    }
    if (variant === 'approve') {
      return {
        variant: buttonVariant ?? 'success',
      }
    }
    if (variant === 'download') {
      return {
        variant: buttonVariant ?? 'outline',
        icon: ['fa', 'arrow-to-bottom'],
      }
    }
    return {
      variant: buttonVariant ?? 'fill',
    }
  }, [buttonVariant, variant])

  const confirmationVariant: ConfirmationVariant = useMemo(() => {
    switch (variant) {
      case 'delete':
        return 'delete'
      case 'deactivate':
        return 'deactivate'
      case 'reactivate':
        return 'reactivate'
      case 'cancel':
        return 'discard'
      case 'approve':
        return 'approve'
      case 'reject':
        return 'reject'
      case 'change':
        return 'change'
      default:
        return 'save'
    }
  }, [variant])

  const handleClick = () => {
    if (withConfirmation) {
      return setConfirmationModal({
        name: name,
        variant: confirmationVariant,
        submit: () => onClick?.(),
      })
    }
    onClick?.()
  }

  if (!isVisible) {
    return null
  }

  if (isIconOnly) {
    return (
      <IconButton
        variant={variant === 'delete' ? 'criticalGhost' : 'ghost'}
        icon={iconProps.icon}
        iconColor={iconProps.iconColor}
        onClick={handleClick}
        {...props}
      />
    )
  }

  return (
    <>
      <Button {...props} {...iconProps} onClick={handleClick}>
        {props.children}
      </Button>
    </>
  )
}

export default ActionButton
